import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Skeleton } from './ui/skeleton';
import { getCurrentPosition } from '../services/geolocation';
import { searchNearbyBusStops, searchBusStopsByQuery } from '../services/overpassApi';
import { formatDistance } from '../services/geolocation';
import { MapPin, Search, Loader2, Navigation, X, List } from 'lucide-react';

const EnhancedLocationSelector = ({ 
  value, 
  onSelect, 
  placeholder = "選擇巴士站",
  showMap = true,
  showSearch = true 
}) => {
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState(showMap ? 'map' : 'search');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nearbyStops, setNearbyStops] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const searchTimeoutRef = React.useRef(null);

  // 獲取用戶位置並搜索附近巴士站
  const handleGetLocation = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const position = await getCurrentPosition();
      setUserLocation(position);
      
      const stops = await searchNearbyBusStops(
        position.latitude, 
        position.longitude, 
        1000
      );
      setNearbyStops(stops);
      setActiveTab('map');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 搜索巴士站
  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const results = await searchBusStopsByQuery(query, 20);
      setSearchResults(results);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 處理巴士站選擇
  const handleBusStopSelect = (busStop) => {
    onSelect(busStop.name);
    setShowModal(false);
  };

  // 處理搜索輸入變化（防抖）
  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(query);
    }, 500);
  };

  // 打開模態框
  const handleOpenModal = () => {
    setShowModal(true);
    if (showMap && !userLocation) {
      handleGetLocation();
    }
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div>
      {/* 輸入框 */}
      <div className="flex gap-2">
        <Input
          type="text"
          value={value}
          onChange={(e) => onSelect(e.target.value)}
          placeholder={placeholder}
          readOnly
          className="flex-1"
        />
        <Button
          type="button"
          variant="outline"
          onClick={handleOpenModal}
        >
          <MapPin className="h-4 w-4 mr-2" />
          選擇位置
        </Button>
      </div>

      {/* 模態框 */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
          <div className="p-6 pb-0">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="flex items-center gap-2">
                  <Navigation className="h-5 w-5" />
                  選擇巴士站
                </DialogTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowModal(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </DialogHeader>
            
            {error && (
              <Alert variant="destructive" className="mb-4 mt-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mt-4">
              <TabsList className="grid w-full grid-cols-2">
                {showMap && (
                  <TabsTrigger value="map">
                    <MapPin className="h-4 w-4 mr-2" />
                    地圖搜索
                  </TabsTrigger>
                )}
                {showSearch && (
                  <TabsTrigger value="search">
                    <Search className="h-4 w-4 mr-2" />
                    文字搜索
                  </TabsTrigger>
                )}
              </TabsList>

              {/* 地圖搜索 */}
              {showMap && (
                <TabsContent value="map" className="mt-4 space-y-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGetLocation}
                    disabled={loading}
                    className="w-full"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        定位中...
                      </>
                    ) : (
                      <>
                        <Navigation className="h-4 w-4 mr-2" />
                        獲取當前位置
                      </>
                    )}
                  </Button>

                  {/* 附近巴士站列表 */}
                  {nearbyStops.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <List className="h-4 w-4" />
                        附近巴士站 ({nearbyStops.length})
                      </div>
                      <div className="max-h-[300px] overflow-y-auto space-y-2 border rounded-lg p-2">
                        {nearbyStops.map((stop) => (
                          <div
                            key={stop.id}
                            className="p-3 border rounded-lg cursor-pointer hover:bg-muted transition-colors"
                            onClick={() => handleBusStopSelect(stop)}
                          >
                            <div className="font-medium">{stop.name}</div>
                            <div className="text-sm text-muted-foreground mt-1">
                              {stop.distance && (
                                <span className="mr-3">
                                  距離: {formatDistance(stop.distance)}
                                </span>
                              )}
                              {stop.tags?.ref && (
                                <span className="mr-3">
                                  <span className="font-medium">#</span>
                                  {stop.tags.ref}
                                </span>
                              )}
                              {stop.tags?.operator && (
                                <span>{stop.tags.operator}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!loading && nearbyStops.length === 0 && userLocation && (
                    <Alert>
                      <AlertDescription>附近未找到巴士站，請嘗試擴大搜索範圍或使用文字搜索</AlertDescription>
                    </Alert>
                  )}
                </TabsContent>
              )}

              {/* 文字搜索 */}
              {showSearch && (
                <TabsContent value="search" className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <Label>搜索巴士站</Label>
                    <Input
                      type="text"
                      placeholder="輸入巴士站名稱..."
                      value={searchQuery}
                      onChange={handleSearchInputChange}
                    />
                  </div>

                  {loading && (
                    <div className="space-y-2">
                      <Skeleton className="h-16 w-full" />
                      <Skeleton className="h-16 w-full" />
                      <Skeleton className="h-16 w-full" />
                    </div>
                  )}

                  {searchResults.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        搜索結果 ({searchResults.length})
                      </p>
                      <div className="max-h-[300px] overflow-y-auto space-y-2 border rounded-lg p-2">
                        {searchResults.map((stop) => (
                          <div
                            key={stop.id}
                            className="p-3 border rounded-lg cursor-pointer hover:bg-muted transition-colors"
                            onClick={() => handleBusStopSelect(stop)}
                          >
                            <div className="font-medium">{stop.name}</div>
                            <div className="text-sm text-muted-foreground mt-1">
                              {stop.tags?.ref && (
                                <span className="mr-3">
                                  <span className="font-medium">#</span>
                                  {stop.tags.ref}
                                </span>
                              )}
                              {stop.tags?.operator && (
                                <span>{stop.tags.operator}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {searchQuery && !loading && searchResults.length === 0 && (
                    <Alert>
                      <AlertDescription>
                        <div className="space-y-2">
                          <p>未找到匹配的巴士站</p>
                          <p className="text-xs text-muted-foreground">
                            💡 提示：Overpass API 對中文搜索支持有限，建議：
                          </p>
                          <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                            <li>嘗試使用英文名稱或站台編號</li>
                            <li>使用地圖搜索功能（點擊「地圖搜索」標籤）</li>
                            <li>縮短搜索關鍵詞</li>
                          </ul>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </TabsContent>
              )}
            </Tabs>
          </div>

          <DialogFooter className="p-6 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
              取消
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnhancedLocationSelector;
